import { siteIdentifier } from '~/utils/siteIdentifier'
import { watch } from 'vue'
import {
  v1CepApiControllerGetAllAccountSites,
  v1CepApiControllerGetUserOpportunities,
  type ContactDto,
  type GetUserOpportunitiesResponseDto,
  type GeneratePdfRequest,
  v1ProjectsControllerCreate,
  v1ProjectsControllerAddImageToProject,
  v1ProjectsControllerAddMembers,
  v1ProjectsControllerDelete,
  v1ProjectsControllerDeleteMembers,
  v1ProjectsControllerGetMapMarkers,
  v1ProjectsControllerGetAccountSites,
  v1ProjectsControllerGetMembers,
  v1ProjectsControllerGetProjectImage,
  v1ProjectsControllerList,
  v1ProjectsControllerRead,
  v1ProjectsControllerInviteMembers,
  v1ProjectsControllerUpdate,
  v1ReportControllerUploadPdf,
  v1ReportControllerGeneratePdf,
  v1RiskValueChainControllerList,
  v1RiskValueChainControllerRead,
  v1SolutionImageControllerDeleteSolutionImage,
  v1SolutionImageControllerGetBatchSolutionImages,
  v1SolutionImageControllerGetSolutionImages,
  v1SolutionImageControllerUploadSolutionImage,
  v1SolutionTypeControllerGetCategories,
  v1SolutionTypeControllerList,
  v1SolutionTypeControllerPreview,
  v1SolutionTypeControllerRead,
  type CreateProjectRequest,
  type CreateProjectMembersRequest,
  type MapMarkerProjectResponse,
  type WorkspaceSummaryResponse,
  type SiteDto,
  type UpdateProjectRequest,
  v1UserControllerGetMyUser,
  v1UserControllerGetMyUserWorkspaces,
  v1UserControllerGetUserByEntraId,
} from '~/generated/api'
import { client } from '~/generated/api/client.gen'
import { AnalyticsLogger } from '~/utils/analytics'
import {
  isAmbiguousInviteOutcome,
  matchMemberByEmail,
  resolveInviteResult,
} from '~/utils/projectMemberInvite'
import { getProjectUpdateErrorKey } from '~/utils/projectUpdateErrors'
import { apiErrorDetails, isExpectedReadFailure, isLocalAuthenticationMissing, withUploadSafeName, type ApiErrorDetails } from '~/utils/apiError'
import { reportApiFailure } from '~/utils/reportApiFailure'
import { normalizeSolutionType } from '~/models'
import type {
  AddProjectMemberResult,
  DeleteProjectMembersRequest,
  ManagedUser,
  ProjectPatchRequest,
  ProjectMember,
  Project,
  SolutionImage,
  SolutionTypeCategory,
  SolutionType,
  RiskValueChain,
  SortByOptions,
  UploadToMySecuritasRequest,
} from '~/models'

interface ApiResult<T> {
  data?: T
  error?: unknown
  response?: Response
}

interface ReadState {
  details?: ApiErrorDetails
  degraded?: boolean
}

interface PendingMapRequest {
  key: string
  generation: number
  request: number
  promise: Promise<void>
}

export const useProjectStore = defineStore('project', () => {
  const appConfig = useAppConfig()
  const opportunities = ref<GetUserOpportunitiesResponseDto[]>([])
  const accountSites = ref<SiteDto[]>([])
  const accountSitesAccountNumber = ref<string | null>(null)
  let accountSitesContextKey: string | null = null
  const projectMapMarkers = ref<MapMarkerProjectResponse[]>([])
  let projectMapMarkersCacheKey = ''
  const mapDataGeneration = ref(0)
  // A public payload owns the active map until selection change or reset.
  // Authenticated fetchers must not replace that payload.
  const publicMapDataLoaded = ref(false)
  let sitesRequest = 0
  let markersRequest = 0
  let pendingSites: PendingMapRequest | undefined
  let pendingMarkers: PendingMapRequest | undefined
  let requestedProjectId: string | undefined
  let projectSelectionRequest = 0
  const projectsFetchInflight = new Map<string, Promise<void>>()
  const riskValueChains = ref<RiskValueChain[]>([])
  const projectTitle = ref('')
  const status = ref('')
  const isConfidential = ref(false)

  const tempCoverImage = ref<File | null>(null)

  const projects = ref<Project[]>([])
  // Opening one project adds it to `projects` before any list has loaded, so
  // a non-empty array alone does not mean the project list is there.
  const projectListLoaded = ref(false)
  const ownProjects = ref<boolean>(true)
  const selectedProject = ref<Project | null>(null)
  const selectedAccountSite = ref<SiteDto | null>(null)
  const selectedRiskValueChain = ref<RiskValueChain | null>(null)
  const selectedOpportunity = ref<GetUserOpportunitiesResponseDto | null>(null)
  // Invalidate synchronously, before any report watcher can reuse the old data.
  // A multi-source watch compares each getter's value on its own, so replacing
  // `selectedProject` with an object carrying the same identifiers — the cover
  // image update does exactly that — does not reset the map.
  watch(
    [
      () => selectedProject.value?.id ?? null,
      () => selectedProject.value?.opportunityId ?? null,
      () => selectedProject.value?.opportunity?.account?.id ?? null,
      () => selectedProject.value?.opportunity?.account?.accountNumber ?? null,
      () => selectedOpportunity.value?.id ?? null,
      () => selectedOpportunity.value?.account?.accountNumber ?? null,
    ],
    resetMapData,
    { flush: 'sync' },
  )

  // Synchronous UI assignments (for example the account picker) also supersede
  // pending requests and must invalidate the ownership recorded by the cache.
  watch(accountSites, () => {
    sitesRequest++
    accountSitesAccountNumber.value = null
    publicMapDataLoaded.value = false
  }, { flush: 'sync' })
  watch(projectMapMarkers, () => {
    markersRequest++
    projectMapMarkersCacheKey = ''
    publicMapDataLoaded.value = false
  }, { flush: 'sync' })

  function resetMapData() {
    mapDataGeneration.value++
    sitesRequest++
    markersRequest++
    accountSites.value = []
    accountSitesAccountNumber.value = null
    projectMapMarkers.value = []
    projectMapMarkersCacheKey = ''
    publicMapDataLoaded.value = false
  }

  function setPublicMapData(sites: SiteDto[], markers: MapMarkerProjectResponse[]) {
    resetMapData()
    accountSites.value = sites
    projectMapMarkers.value = markers
    publicMapDataLoaded.value = true
  }

  const selectedOpportunityContactPerson = ref<ContactDto | null>(null)
  const selectedSortBy = ref<SortByOptions>()

  // Dictionary to store solution types by workspace
  const solutionTypesByWorkspace = ref<Record<string, SolutionType[]>>({})

  const draftProjects = ref<Project[]>([])
  const inProgressProjects = ref<Project[]>([])
  const completedProjects = ref<Project[]>([])

  const pending = ref(false)
  const error = ref<string | null>(null)
  const customError = ref<string | null>(null)
  const customErrorParams = ref<Record<string, string>>({})
  watch(customError, () => {
    customErrorParams.value = {}
  }, { flush: 'sync' })
  // The API's error ID for the error shown, for users to quote to support.
  const errorReference = ref<string | null>(null)
  // Why the last read through getRequest failed, for callers that handle null.
  const lastReadFailure = ref<ApiErrorDetails | null>(null)
  const customSuccessMessage = ref<string | null>(null)
  const showNotification = ref(false)
  const projectAccessError = ref(false)
  // Why the selected project could not be loaded, as a translation key.
  const projectLoadErrorKey = ref<string | null>(null)
  const shouldRefetch = ref(false)
  const projectListDegraded = ref(false)
  const degradedProjects = new Set<string>()

  const allSolutionTypes = ref<SolutionType[]>([])
  const allSolutionTypeCategories = ref<SolutionTypeCategory[]>([])

  const userWorkspaces = ref<Pick<WorkspaceSummaryResponse, 'id' | 'name'>[]>([])

  // caching
  const cache = ref(new Map<string, any>())
  const CACHE_DURATION = 10 * 60 * 1000 // 10 minutes in milliseconds
  const pendingSolutionTypes = new Map<string, Promise<void>>()

  function notifyRequestError(requestError: unknown, responseStatus?: number) {
    const details = apiErrorDetails(requestError, responseStatus)
    reportApiFailure(details, 'project_store_request')
    customError.value = getProjectUpdateErrorKey(details)
    errorReference.value = details.exceptionId ?? null
    customSuccessMessage.value = null
    error.value = requestError instanceof Error
      ? requestError.message
      : String(requestError)
    showNotification.value = true
  }

  let cepOutageNoticed = false
  /** Tells the user once when CEP's outage left opportunity details out. */
  function noticeDegradedResponse(response: Response | undefined) {
    const degraded = response?.headers?.get?.('Advisor-Degraded') ?? ''
    if (!degraded.includes('cep_unavailable') || cepOutageNoticed)
      return
    cepOutageNoticed = true
    customError.value = 'cep_details_unavailable'
    errorReference.value = null
    showNotification.value = true
  }

  /**
   * Runs a read. A failed read answers null (a 4xx) or undefined (anything
   * else, or an aborted request); pass `failure` to learn why this read
   * failed, since concurrent reads share lastReadFailure.
   */
  async function getRequest<T>(
    fetching: Ref<boolean>,
    request: () => Promise<ApiResult<T>>,
    signal?: AbortSignal,
    failure?: ReadState,
  ): Promise<T | null | undefined> {
    fetching.value = true
    // A notification on screen belongs to an earlier request: clearing its
    // message would turn it into a success message until it closes.
    if (!showNotification.value) {
      error.value = null
      customError.value = null
      errorReference.value = null
    }
    lastReadFailure.value = null
    const recordFailure = (details: ApiErrorDetails) => {
      lastReadFailure.value = details
      if (failure)
        failure.details = details
    }
    try {
      const result = await request()
      if (result.error !== undefined) {
        if (!result.response && isLocalAuthenticationMissing(result.error))
          return undefined
        if (signal?.aborted)
          return undefined

        const statusCode = result.response?.status ?? 0
        if (statusCode >= 400 && statusCode < 500) {
          // null tells the caller the read failed; lastReadFailure says why.
          const details = apiErrorDetails(result.error, statusCode)
          recordFailure(details)
          // A missing item or an admin-only list is a state the caller shows.
          if (!isExpectedReadFailure(details))
            notifyRequestError(result.error, statusCode)
          return null
        }

        recordFailure(apiErrorDetails(result.error, statusCode))
        notifyRequestError(result.error, statusCode)
        return undefined
      }
      if (failure) {
        failure.degraded = result.response?.headers.get('Advisor-Degraded')
          ?.includes('cep_unavailable') ?? false
      }
      noticeDegradedResponse(result.response)
      return result.data
    }
    catch (requestError) {
      // No request was sent. MSAL handles sign-in; keep the current view while
      // it redirects instead of displaying an expired-session or access error.
      if (isLocalAuthenticationMissing(requestError))
        return undefined
      if (!signal?.aborted) {
        recordFailure(apiErrorDetails(requestError))
        notifyRequestError(requestError)
      }
      return undefined
    }
    finally {
      fetching.value = false
    }
  }

  async function mutationRequest<T>(
    loading: Ref<boolean>,
    request: () => Promise<ApiResult<T>>,
  ): Promise<{
    data: T | undefined
    success: boolean
    statusCode?: number
    error?: unknown
  }> {
    loading.value = true
    error.value = null
    try {
      const result = await request()
      if (result.error !== undefined) {
        notifyRequestError(result.error, result.response?.status)
        return {
          data: undefined,
          success: false,
          statusCode: result.response?.status,
          error: result.error,
        }
      }
      return {
        data: result.data,
        success: true,
        statusCode: result.response?.status,
      }
    }
    catch (requestError) {
      notifyRequestError(requestError)
      // No response at all — a network or CORS failure rather than a status.
      return { data: undefined, success: false, error: requestError }
    }
    finally {
      loading.value = false
    }
  }

  // Clear cache function
  function clearCache(endpoint?: string) {
    if (endpoint) {
      cache.value.delete(endpoint)
    }
    else {
      cache.value.clear()
    }
  }

  function setImage(file: File) {
    tempCoverImage.value = file
  }

  function resetImage() {
    tempCoverImage.value = null
  }

  function resetNewProject() {
    resetMapData()
    projectTitle.value = ''
    status.value = ''
    isConfidential.value = false
    pending.value = false
    selectedOpportunity.value = null
    selectedAccountSite.value = null
    selectedRiskValueChain.value = null
    opportunities.value = []
    riskValueChains.value = []
  }

  function reset() {
    resetMapData()
    projectTitle.value = ''
    status.value = ''
    isConfidential.value = false
    pending.value = false
    selectedOpportunity.value = null
    selectedAccountSite.value = null
    selectedRiskValueChain.value = null
    error.value = null
    customError.value = null
    errorReference.value = null
    customSuccessMessage.value = null
    showNotification.value = false
    projectAccessError.value = false
    projectLoadErrorKey.value = null
    opportunities.value = []
    riskValueChains.value = []
    projects.value = []
    projectListLoaded.value = false
    projectListDegraded.value = false
    degradedProjects.clear()
    cepOutageNoticed = false
    riskValueChains.value = []
  }

  async function fetchProjects(
    fetching: Ref<boolean>,
    id?: string,
    accessToken?: string,
    userId?: string,
    signal?: AbortSignal,
  ) {
    if (id && id !== requestedProjectId) {
      requestedProjectId = id
      projectSelectionRequest++
      resetMapData()
    }
    const selectionRequest = projectSelectionRequest
    const selectionGeneration = mapDataGeneration.value
    const isCurrentSelection = () =>
      !signal?.aborted && (!id || (
        selectionRequest === projectSelectionRequest
        && selectionGeneration === mapDataGeneration.value
      ))

    // reset access error state before a new fetch
    projectAccessError.value = false
    projectLoadErrorKey.value = null

    if (
      projects.value.length > 0
      && projectListLoaded.value
      && !id
      && !ownProjects.value
      && !shouldRefetch.value
      && !projectListDegraded.value
    ) {
      return
    }

    if (
      id
      && projects.value
      && projects.value.length > 0
      && !shouldRefetch.value
      && !degradedProjects.has(id)
    ) {
      const existingProject = projects.value.find(
        project => project.id === id,
      )
      // if existing project has full data, set it as selected project
      if (existingProject && existingProject.areas) {
        selectedProject.value = existingProject
        return
      }
    }

    // if the single project is already loaded and saved in selectedProject, return
    if (
      id
      && projects.value
      && projects.value.length === 0
      && selectedProject.value
      && selectedProject.value.id === id
      && !shouldRefetch.value
      && !degradedProjects.has(id)
    ) {
      return
    }

    const inflightKey = id ? JSON.stringify([id, selectionRequest, selectionGeneration]) : ''
    const inflight = projectsFetchInflight.get(inflightKey)
    if (inflight) {
      await inflight
      return
    }

    const request = (async () => {
      const auth = accessToken ? { auth: accessToken } : {}
      const readFailure: ReadState = {}
      const data = id
        ? await getRequest(
            fetching,
            () => v1ProjectsControllerRead({
              ...auth,
              path: { id },
              signal,
            }),
            signal,
            readFailure,
          )
        : await getRequest(
            fetching,
            () => v1ProjectsControllerList({
              ...auth,
              // OpenAPI is missing the supported userId project-list query.
              ...(userId ? { query: { userId } } : {}),
              signal,
            }),
            signal,
            readFailure,
          )

      if (!isCurrentSelection())
        return

      // A specific project that did not load: say why instead of showing an
      // empty project. An aborted request (undefined, no failure) just stops.
      const failure = readFailure.details
      if (id && (data === null || (data === undefined && failure))) {
        projectAccessError.value = true
        // The API answers 404 both for a missing project and for one the
        // caller may not see.
        projectLoadErrorKey.value = failure?.statusCode === 404 || !failure
          ? 'project_access_denied_not_in_cep_sales_team'
          : getProjectUpdateErrorKey(failure)
        // getRequest has already shown any failure other than "not found".
        if (!failure || failure.statusCode === 404) {
          customError.value = projectLoadErrorKey.value
          errorReference.value = failure?.exceptionId ?? null
          showNotification.value = true
        }
        selectedProject.value = null
        return
      }

      // If the request was aborted, data will be undefined, so return early
      if (data === undefined || data === null)
        return

      // Retry degraded results on the next visit rather than retaining a
      // partial list or stale opportunity details for the rest of the session.
      if (id) {
        if (readFailure.degraded)
          degradedProjects.add(id)
        else
          degradedProjects.delete(id)
      }
      else {
        projectListDegraded.value = readFailure.degraded ?? false
      }
      if (!projectListDegraded.value && degradedProjects.size === 0)
        cepOutageNoticed = false

      // reset the shouldRefetch flag after fetching projects
      if (shouldRefetch.value)
        shouldRefetch.value = false

      if (id) {
        const fetchedProject = data?.projects?.[0] as unknown as Project | null
        if (fetchedProject?.image) {
          const image = await fetchProjectCoverImage(
            id,
            fetching,
            accessToken,
            signal,
          )
          // Replace the image metadata with the blob URL. When the cover
          // fetch fails, clear it so `<img :src>` never receives the metadata
          // object (which renders as `[object Object]`).
          fetchedProject.image = image
        }
        if (!isCurrentSelection())
          return

        // if load a single project, wait until the image is loaded
        selectedProject.value = fetchedProject
        // update the projects data with full data
        if (fetchedProject) {
          const index = projects.value.findIndex(project => project.id === id)
          if (index !== -1) {
            projects.value[index] = fetchedProject
          }
          else {
            projects.value.push(fetchedProject)
            sortProjects()
          }
        }
      }
      else {
        // if loading projects, not wait until the cover image is loaded
        // images are fetched on demand via IntersectionObserver in ProjectCard
        projects.value = (data?.projects ?? []) as unknown as Project[]
        projectListLoaded.value = true
        sortProjects()
      }
    })()

    projectsFetchInflight.set(inflightKey, request)
    try {
      await request
    }
    finally {
      projectsFetchInflight.delete(inflightKey)
    }
  }

  async function fetchProjectCoverImage(
    projectId: string,
    fetching?: Ref<boolean>,
    accessToken?: string,
    signal?: AbortSignal,
  ) {
    const webReportStore = useWebReportStore()
    const publicCoverUrl = webReportStore.publicAssetUrl(
      'cover-image',
      projectId,
    )

    if (publicCoverUrl) {
      const busy = fetching ?? ref(false)
      busy.value = true
      try {
        const blob = await $fetch<Blob>(publicCoverUrl, {
          responseType: 'blob',
          signal,
        })
        return blob ? URL.createObjectURL(blob) : undefined
      }
      catch {
        return undefined
      }
      finally {
        busy.value = false
      }
    }

    const blob = await getRequest(
      fetching ?? ref(false),
      () => v1ProjectsControllerGetProjectImage({
        ...(accessToken ? { auth: accessToken } : {}),
        parseAs: 'blob',
        path: { id: projectId },
        signal,
      }) as Promise<ApiResult<Blob>>,
      signal,
    )
    return blob ? URL.createObjectURL(blob) : undefined
  }

  async function fetchOpportunities(fetching: Ref<boolean>) {
    if (opportunities.value.length > 0)
      return

    const data = await getRequest(fetching, () =>
      v1CepApiControllerGetUserOpportunities())

    // remove opportunities without account
    opportunities.value = ((data?.opportunities ?? []) as GetUserOpportunitiesResponseDto[]).filter(
      opportunity => opportunity.account,
    )
  }

  async function fetchContactPerson(fetching: Ref<boolean>, id?: string) {
    if (!selectedOpportunity.value && !id)
      return

    const opportunityId = id ?? selectedOpportunity.value?.id
    const data = await getRequest(fetching, () =>
      client.get<Record<number, ContactDto>>({
        security: [{ scheme: 'bearer', type: 'http' }],
        url: `/opportunities/${opportunityId}/contact`,
      }))

    selectedOpportunityContactPerson.value = data as ContactDto
  }

  async function waitForMapRequest(fetching: Ref<boolean>, promise: Promise<void>) {
    fetching.value = true
    try {
      await promise
    }
    finally {
      fetching.value = false
    }
  }

  async function fetchAccountAccountSites(
    fetching: Ref<boolean>,
    accountNumber?: string,
    opportunityId?: string,
  ) {
    if (publicMapDataLoaded.value)
      return
    const resolvedAccountNumber
      = accountNumber ?? selectedOpportunity.value?.account?.accountNumber
    if (!resolvedAccountNumber)
      return
    const project = selectedProject.value
    const projectId = project?.opportunity?.account?.accountNumber === resolvedAccountNumber
      && (!opportunityId || project.opportunityId === opportunityId)
      ? project.id
      : undefined
    const contextKey = JSON.stringify({ accountNumber: resolvedAccountNumber, projectId })
    if (
      pendingSites?.key === contextKey
      && pendingSites.generation === mapDataGeneration.value
      && pendingSites.request === sitesRequest
    ) {
      await waitForMapRequest(fetching, pendingSites.promise)
      return accountSitesAccountNumber.value === resolvedAccountNumber
    }
    sitesRequest++
    if (
      accountSites.value.length > 0
      && accountSitesAccountNumber.value === resolvedAccountNumber
      && accountSitesContextKey === contextKey
    ) {
      return true
    }

    accountSites.value = []
    accountSitesAccountNumber.value = null
    const request = sitesRequest
    const generation = mapDataGeneration.value
    const promise = (async () => {
      const data = await getRequest(ref(false), () => projectId
        ? v1ProjectsControllerGetAccountSites({ path: { id: projectId } })
        : v1CepApiControllerGetAllAccountSites({ path: { accountNumber: resolvedAccountNumber } }))

      if (request !== sitesRequest || generation !== mapDataGeneration.value)
        return

      accountSites.value = data?.sites ?? []
      accountSitesAccountNumber.value = data ? resolvedAccountNumber : null
      accountSitesContextKey = data ? contextKey : null
    })()
    pendingSites = { key: contextKey, generation, request, promise }
    try {
      await waitForMapRequest(fetching, promise)
      return accountSitesAccountNumber.value === resolvedAccountNumber
    }
    finally {
      if (pendingSites?.promise === promise)
        pendingSites = undefined
    }
  }

  async function fetchProjectMapMarkers(
    fetching: Ref<boolean>,
    params: {
      opportunityId?: string
      accountNumber?: string
      accountId?: string
    },
  ) {
    if (publicMapDataLoaded.value)
      return
    if (!params.opportunityId && !params.accountNumber && !params.accountId)
      return

    const cacheKey = JSON.stringify({
      opportunityId: params.opportunityId,
      accountNumber: params.accountNumber,
      accountId: params.accountId,
    })
    if (
      pendingMarkers?.key === cacheKey
      && pendingMarkers.generation === mapDataGeneration.value
      && pendingMarkers.request === markersRequest
    ) {
      await waitForMapRequest(fetching, pendingMarkers.promise)
      return
    }
    markersRequest++
    if (
      projectMapMarkersCacheKey === cacheKey
      && projectMapMarkers.value.length > 0
    ) {
      return
    }

    projectMapMarkers.value = []
    const request = markersRequest
    const generation = mapDataGeneration.value
    const promise = (async () => {
      // OpenAPI is missing all supported map-marker filters.
      const data = await getRequest(ref(false), () =>
        v1ProjectsControllerGetMapMarkers({ query: params }))

      if (request !== markersRequest || generation !== mapDataGeneration.value)
        return

      projectMapMarkers.value = data?.mapMarkers ?? []
      projectMapMarkersCacheKey = cacheKey
    })()
    pendingMarkers = { key: cacheKey, generation, request, promise }
    try {
      await waitForMapRequest(fetching, promise)
    }
    finally {
      if (pendingMarkers?.promise === promise)
        pendingMarkers = undefined
    }
  }

  async function fetchRiskValueChains(fetching: Ref<boolean>, id?: string) {
    if (riskValueChains.value.length > 0 && !id) {
      return
    }
    if (id) {
      const data = await getRequest(fetching, () =>
        v1RiskValueChainControllerRead({ path: { id } }))
      selectedRiskValueChain.value = data as unknown as RiskValueChain
    }
    else {
      const data = await getRequest(fetching, () =>
        v1RiskValueChainControllerList())
      riskValueChains.value = (data?.riskValueChains as unknown as RiskValueChain[] ?? [])
        .sort((a, b) => a.name.localeCompare(b.name))
    }
  }

  async function createProject(posting: Ref<boolean>) {
    const opportunityId = isConfidential.value
      ? undefined
      : selectedOpportunity.value?.id
    const accountSiteId = isConfidential.value
      ? undefined
      : siteIdentifier(selectedAccountSite.value)
    const requestBody = {
      name: projectTitle.value,
      status: status.value,
      opportunityId,
      riskValueChainId: selectedRiskValueChain.value?.id,
      accountSiteId,
      isConfidential: isConfidential.value,
    }
    posting.value = true

    try {
      const { data } = await v1ProjectsControllerCreate({
        body: requestBody as CreateProjectRequest,
        throwOnError: true,
      })
      const project = data
      if (project) {
        // refetch again is because the response of POST /projects lacks opportunity field
        await fetchProjects(posting, project.id)

        // upload the picture
        await uploadPicture(project.id, posting)

        showNotification.value = true
        return selectedProject.value
      }
    }
    catch (err) {
      error.value = (err as Error).message
      showNotification.value = true
    }
    finally {
      posting.value = false
    }
  }

  // Function to upload a picture
  async function uploadPicture(projectId: string, posting: Ref<boolean>) {
    // Check if there is a picture to upload
    if (tempCoverImage.value) {
      await mutationRequest(posting, () =>
        v1ProjectsControllerAddImageToProject({
          body: { image: withUploadSafeName(tempCoverImage.value!) },
          headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
          path: { id: projectId },
        }))

      // Update the selected project's image with the new URL
      if (selectedProject.value)
        selectedProject.value.image = URL.createObjectURL(tempCoverImage.value)
    }
  }

  async function updateProject(
    patching: Ref<boolean>,
    requestBody: ProjectPatchRequest,
    id?: string,
  ) {
    if (!selectedProject.value && !id)
      return

    patching.value = true
    error.value = null
    customError.value = null
    errorReference.value = null
    customSuccessMessage.value = null
    showNotification.value = false

    try {
      const { data: responseData } = await v1ProjectsControllerUpdate({
        body: requestBody as UpdateProjectRequest,
        path: { id: id ?? selectedProject.value!.id },
        throwOnError: true,
      })
      const data = responseData as unknown as Project
      if (data && selectedProject.value) {
        selectedProject.value.name = requestBody.name ?? data.name
        selectedProject.value.status = requestBody.status ?? data.status
        // The backend may store a different reference to the same site than
        // the one sent, such as the account list's GUID for a single site.
        selectedProject.value.accountSiteId
          = data.accountSiteId !== undefined ? data.accountSiteId : requestBody.accountSiteId
        selectedProject.value.updatedAt = data.updatedAt
        selectedProject.value.uploadedToMySecuritas
          = requestBody.uploadedToMySecuritas ?? data.uploadedToMySecuritas
        if (requestBody.opportunityId !== undefined) {
          selectedProject.value.opportunityId
            = requestBody.opportunityId ?? data.opportunityId
        }
        if (requestBody.opportunityId !== undefined || requestBody.accountSiteId !== undefined) {
          selectedProject.value.opportunity
            = requestBody.opportunityId === null
              ? null
              : (data.opportunity ?? selectedProject.value.opportunity)
        }
        // upload the picture
        await uploadPicture(data.id, patching)
        sortProjects()
      }
      return data
    }
    catch (err) {
      error.value = (err as Error).message
      const details = apiErrorDetails(err)
      reportApiFailure(details, 'update_project')
      // A raw translation key: Notification.vue translates customError.
      customError.value = getProjectUpdateErrorKey(details)
      errorReference.value = details.exceptionId ?? null
      // The change was saved; only reading it back failed, so reload rather
      // than invite the user to repeat it.
      if (details.code === 'project_saved_reload_failed')
        shouldRefetch.value = true
    }
    finally {
      showNotification.value = true
      patching.value = false
    }
  }

  async function sortProjects() {
    projects.value.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  }

  async function deleteProject(
    deleting: Ref<boolean>,
    projectId: string,
    projectName?: string,
  ) {
    AnalyticsLogger.send('project_delete_button_clicked', {
      item_name:
        selectedProject.value?.name ?? projectName ?? 'Unknown Project',
      risk_value_chain_name: selectedProject.value?.riskValueChain?.name,
      project_name: selectedProject.value?.name ?? projectName,
      status: 'Deleted',
    })
    if (!projectId) {
      return
    }

    const result = await mutationRequest(deleting, () =>
      v1ProjectsControllerDelete({
        headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
        path: { id: projectId },
      }))
    if (result.success) {
      projects.value = projects.value.filter(
        project => project.id !== projectId,
      )
      customSuccessMessage.value = 'project_deleted_successfully'
      showNotification.value = true
      navigateTo('/projects')
    }
  }

  async function fetchSolutionTypePreview(
    fetching: Ref<boolean>,
    workspaceId?: string,
  ): Promise<SolutionType[]> {
    const data = await getRequest(fetching, () =>
      v1SolutionTypeControllerPreview({
        ...(workspaceId ? { query: { workspaceId } } : {}),
      }))
    const solutions = (data?.solutionTypes ?? []).map(normalizeSolutionType)
    if (!solutions.length)
      return []
    const images = await getBatchSolutionImages(
      fetching,
      solutions.map(solution => solution.id),
    )
    return solutions.map(solution => ({
      ...solution,
      images: images[solution.id] ?? [],
    }))
  }

  async function fetchAllSolutionTypes(
    fetching: Ref<boolean>,
    workspaceId?: string,
  ) {
    const fetchEndpoint = workspaceId
      ? `solution-types?workspaceId=${workspaceId}`
      : 'solution-types'

    const pending = pendingSolutionTypes.get(fetchEndpoint)
    if (pending) {
      await waitForMapRequest(fetching, pending)
      return
    }

    const request = (async () => {
      const now = Date.now()
      const cached = cache.value.get(fetchEndpoint)
      const cacheIsFresh = cached && now - cached.timestamp < CACHE_DURATION
      const data = cacheIsFresh
        ? cached.data
        : await getRequest(fetching, () =>
            v1SolutionTypeControllerList({
              ...(workspaceId ? { query: { workspaceId } } : {}),
            }))
      if (!cacheIsFresh && data)
        cache.value.set(fetchEndpoint, { data, timestamp: Date.now() })
      if (data) {
        const solutionTypes: SolutionType[] = (data.solutionTypes ?? []).map(
          (solutionType: Parameters<typeof normalizeSolutionType>[0]) =>
            normalizeSolutionType(solutionType),
        )
        allSolutionTypes.value = solutionTypes

        const solutionTypeIds = allSolutionTypes.value.map(st => st.id)
        if (solutionTypeIds.length > 0) {
          const imagesData = await getBatchSolutionImages(
            fetching,
            solutionTypeIds,
          )
          // Map the solution types with their images
          allSolutionTypes.value = solutionTypes.map(solutionType => ({
            ...solutionType,
            images: imagesData[solutionType.id] ?? [],
          }))
        }
      }
    })()
    pendingSolutionTypes.set(fetchEndpoint, request)
    try {
      await request
    }
    finally {
      pendingSolutionTypes.delete(fetchEndpoint)
    }
  }

  async function fetchSolutionType(fetching: Ref<boolean>, id: string) {
    const response = await getRequest(fetching, () =>
      v1SolutionTypeControllerRead({ path: { id } }))
    const solutionType = response ? normalizeSolutionType(response) : response
    if (solutionType) {
      const imagesData = await getBatchSolutionImages(fetching, [
        solutionType.id,
      ])
      solutionType.images = imagesData[solutionType.id] ?? []
    }
    return solutionType
  }

  async function generatePdf(
    fetching: Ref<boolean>,
    html: string,
    fileName: string,
  ) {
    fetching.value = true
    error.value = null
    customError.value = null
    errorReference.value = null

    try {
      const { data } = await v1ReportControllerGeneratePdf({
        body: { html, fileName },
        headers: {
          'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY,
        },
        parseAs: 'blob',
        throwOnError: true,
      })
      const blob = data as Blob

      // Trigger Download
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `${fileName}.pdf`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    }
    catch (err) {
      console.error('Failed to generate PDF:', err)
      customError.value = 'pdf_generation_timeout'
      showNotification.value = true
      return null
    }
    finally {
      fetching.value = false
    }
  }

  async function uploadToMySecuritas(
    posting: Ref<boolean>,
    request: UploadToMySecuritasRequest,
  ) {
    if (!request.siteId) {
      customError.value = 'select_account_site_description'
      showNotification.value = true
      return null
    }
    const body: GeneratePdfRequest = { ...request, siteId: request.siteId }
    const { data: result } = await mutationRequest(posting, () =>
      v1ReportControllerUploadPdf({
        body,
        headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
      }))

    // Update the project's upload status in the local store
    if (result && projects.value.length > 0) {
      await updateProject(
        posting,
        {
          uploadedToMySecuritas: 'UPLOADED',
        } as ProjectPatchRequest,
        request.projectId,
      )
      const project = projects.value.find(p => p.id === request.projectId)
      if (project) {
        project.uploadedToMySecuritas = 'UPLOADED'
      }
    }

    return result
  }

  async function getSolutionImages(
    fetching: Ref<boolean>,
    solutionId: string,
    imageIds: string[] = [],
  ): Promise<SolutionImage[]> {
    const webReportStore = useWebReportStore()

    if (webReportStore.publicAccessToken) {
      fetching.value = false
      return imageIds
        .map(id => ({
          id,
          filename: `${id}.jpg`,
          mimetype: 'image/jpeg',
          downloadUrl:
            webReportStore.publicAssetUrl(`solution-images/${id}`) ?? '',
        }))
        .filter(image => image.downloadUrl)
    }

    const data = await getRequest(fetching, () =>
      v1SolutionImageControllerGetSolutionImages({
        path: { solutionTypeId: solutionId },
      }))
    return (data?.images ?? []) as unknown as SolutionImage[]
  }

  async function getBatchSolutionImages(
    fetching: Ref<boolean>,
    solutionTypeIds: string[],
  ): Promise<Record<string, SolutionImage[]>> {
    const BATCH_SIZE = 2000 // Process solutionTypeIds in batches to avoid firewall blocking
    const result: Record<string, SolutionImage[]> = {}

    // Process solutionTypeIds in batches
    for (let i = 0; i < solutionTypeIds.length; i += BATCH_SIZE) {
      const batch = solutionTypeIds.slice(i, i + BATCH_SIZE)
      try {
        const { data } = await mutationRequest(fetching, () =>
          v1SolutionImageControllerGetBatchSolutionImages({
            body: { solutionTypeIds: batch },
            headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
          }))
        const batchResult = data as unknown as Record<string, SolutionImage[]>

        // Merge batch results into the main result
        Object.assign(result, batchResult)
      }
      catch (error) {
        console.error(
          `Error fetching batch ${Math.floor(i / BATCH_SIZE) + 1}:`,
          error,
        )
        // Continue with next batch even if one fails
      }
    }

    return result
  }

  async function uploadSolutionImage(
    posting: Ref<boolean>,
    solutionId: string,
    media: File,
  ): Promise<SolutionImage> {
    const { data } = await mutationRequest(posting, () =>
      v1SolutionImageControllerUploadSolutionImage({
        body: { image: withUploadSafeName(media) },
        headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
        path: { solutionTypeId: solutionId },
      }))
    return data as unknown as SolutionImage
  }

  async function deleteSolutionImage(
    posting: Ref<boolean>,
    solutionId: string,
    imageId: string,
  ) {
    await mutationRequest(posting, () =>
      v1SolutionImageControllerDeleteSolutionImage({
        headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
        path: { imageId, solutionTypeId: solutionId },
      }))
  }

  async function getSolutionTypeCategories(fetching: Ref<boolean>) {
    const data = await getRequest(fetching, () =>
      v1SolutionTypeControllerGetCategories())
    allSolutionTypeCategories.value = (data?.categories ?? []) as unknown as SolutionTypeCategory[]
  }

  async function fetchUserContext(fetching: Ref<boolean>) {
    return takeAnalyticsUserId(await getRequest(fetching, () =>
      v1UserControllerGetMyUser())) ?? null
  }

  async function fetchUserWorkspaces(fetching: Ref<boolean>) {
    const data = await getRequest(fetching, () =>
      v1UserControllerGetMyUserWorkspaces())
    userWorkspaces.value = data?.workspaces ?? []
  }

  /**
   * Invites a member and reports what actually happened.
   *
   * A bare `data` value cannot distinguish "the request failed" from "the
   * request succeeded but returned no body", and both used to surface as an
   * invalid-email message. The member list is re-read only for the latter,
   * where the 2xx already proves the invite went through. Failures — status or
   * no response — are taken at face value.
   */
  async function addProjectMember(
    posting: Ref<boolean>,
    createProjectMembersRequest: CreateProjectMembersRequest,
  ): Promise<AddProjectMemberResult> {
    const { projectId, memberEmail } = createProjectMembersRequest
    const post = await mutationRequest(posting, () =>
      v1ProjectsControllerAddMembers({
        body: createProjectMembersRequest,
        headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
        path: { id: projectId },
      }))
    const outcome = {
      ...post,
      data: post.data as unknown as ProjectMember | undefined,
    }

    if (!isAmbiguousInviteOutcome(outcome)) {
      return resolveInviteResult(outcome, null)
    }

    posting.value = true
    try {
      const reconciled = await findProjectMemberByEmail(projectId, memberEmail)
      return resolveInviteResult(outcome, reconciled)
    }
    finally {
      posting.value = false
    }
  }

  /**
   * Looks the member up without surfacing its own failure — this runs while an
   * invite is already being reported on, and a failed lookup must leave the
   * original outcome intact rather than replacing it with a lookup error.
   */
  async function findProjectMemberByEmail(
    projectId: string,
    memberEmail: string,
  ): Promise<ProjectMember | null> {
    try {
      const result = await v1ProjectsControllerGetMembers({
        path: { id: projectId },
      })
      if (result.error !== undefined)
        return null
      const members = (result.data?.members ?? []) as unknown as ProjectMember[]
      return matchMemberByEmail(members, memberEmail)
    }
    catch {
      return null
    }
  }

  async function resendProjectMemberInvitation(
    posting: Ref<boolean>,
    createProjectMembersRequest: CreateProjectMembersRequest,
  ) {
    const { data } = await mutationRequest(posting, () =>
      v1ProjectsControllerInviteMembers({
        body: createProjectMembersRequest,
        headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
        path: { id: createProjectMembersRequest.projectId },
      }))
    return data
  }

  async function deleteProjectMember(
    posting: Ref<boolean>,
    deleteProjectMembersRequest: DeleteProjectMembersRequest,
  ) {
    const { data } = await mutationRequest(posting, () =>
      v1ProjectsControllerDeleteMembers({
        headers: { 'admin-api-key': appConfig.ADVISOR_ADMIN_API_KEY },
        path: {
          id: deleteProjectMembersRequest.projectId,
          memberId: deleteProjectMembersRequest.memberId,
        },
      }))
    return data
  }

  async function getProjectMembers(fetching: Ref<boolean>, projectId: string) {
    const data = await getRequest(fetching, () =>
      v1ProjectsControllerGetMembers({ path: { id: projectId } }))
    return (data?.members ?? []) as unknown as ProjectMember[]
  }

  async function getUserByEntraId(fetching: Ref<boolean>, entraId: string) {
    return await getRequest(fetching, () =>
      v1UserControllerGetUserByEntraId({
        path: { entraIdOid: entraId },
      })) as unknown as ManagedUser
  }

  return {
    projectTitle,
    mapDataGeneration,
    publicMapDataLoaded,
    resetMapData,
    setPublicMapData,
    accountSites,
    projectMapMarkers,
    riskValueChains,
    tempCoverImage,
    status,
    isConfidential,
    opportunities,
    selectedProject,
    selectedOpportunity,
    selectedOpportunityContactPerson,
    selectedAccountSite,
    selectedRiskValueChain,
    selectedSortBy,
    draftProjects,
    inProgressProjects,
    completedProjects,
    projects,
    pending,
    error,
    customError,
    customErrorParams,
    errorReference,
    lastReadFailure,
    projectAccessError,
    projectLoadErrorKey,
    showNotification,
    notifyRequestError,
    shouldRefetch,
    projectListDegraded,
    reset,
    resetImage,
    resetNewProject,
    setImage,
    fetchProjects,
    deleteProject,
    fetchOpportunities,
    fetchContactPerson,
    fetchAccountAccountSites,
    fetchProjectMapMarkers,
    fetchRiskValueChains,
    createProject,
    updateProject,
    cache,
    fetchAllSolutionTypes,
    fetchSolutionTypePreview,
    allSolutionTypes,
    customSuccessMessage,
    uploadToMySecuritas,
    ownProjects,
    projectListLoaded,
    uploadSolutionImage,
    getSolutionTypeCategories,
    allSolutionTypeCategories,
    fetchUserContext,
    getSolutionImages,
    getBatchSolutionImages,
    fetchSolutionType,
    deleteSolutionImage,
    fetchUserWorkspaces,
    userWorkspaces,
    addProjectMember,
    deleteProjectMember,
    getProjectMembers,
    resendProjectMemberInvitation,
    solutionTypesByWorkspace,
    getUserByEntraId,
    clearCache,
    fetchProjectCoverImage,
    generatePdf,
  }
})
